import { AnimatePresence, motion as Motion } from 'motion/react';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { colors, accent } from '@/lib/tokens';
import { OnboardingChoice } from '@/components/onboarding/OnboardingChoiceChips';

type OnboardingQuestionSlide = {
  kind: 'question';
  title: string;
  prompt: string;
  choices: OnboardingChoice[];
  value: string | null;
};

type OnboardingMessageSlide = {
  kind: 'message';
  title: string;
  prompt: string;
  body: string;
  primaryAction: { label: string; value: string };
  secondaryAction?: { label: string; value: string };
};

export type OnboardingSlide = OnboardingQuestionSlide | OnboardingMessageSlide;

export function OnboardingSlideshow({
  slide,
  step,
  totalSteps,
  canGoBack = false,
  disabled = false,
  onBack,
  onSkip,
  onSelect,
  otherInputValue,
  onOtherInputChange,
  onOtherInputSubmit,
}: {
  slide: OnboardingSlide;
  step: number;
  totalSteps: number;
  canGoBack?: boolean;
  disabled?: boolean;
  onBack?: () => void;
  onSkip: () => void;
  onSelect: (value: string) => void;
  otherInputValue?: string;
  onOtherInputChange?: (val: string) => void;
  onOtherInputSubmit?: () => void;
}) {
  const progressBar = (
    <div className="flex items-center gap-3">
      <div className="text-[11px] uppercase tracking-[0.16em]" style={{ color: colors.textMuted }}>
        Step {step} / {totalSteps}
      </div>
      <div className="h-1 w-28 overflow-hidden" style={{ background: colors.bgSubtle }}>
        <Motion.div
          className="h-full w-full"
          animate={{ scaleX: step / totalSteps }}
          transition={{ duration: 0.24 }}
          style={{ background: accent, transformOrigin: 'left' }}
        />
      </div>
    </div>
  );

  return (
    <Motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="pointer-events-auto w-full max-w-[640px] flex flex-col overflow-hidden rounded-2xl"
      style={{
        height: '560px',
        background: '#ffffff',
        border: `1px solid ${colors.borderDefault}`,
        boxShadow: '0 16px 60px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(18px)',
      }}
    >
      {/* Header */}
      <div className="flex-shrink-0 flex items-center border-b px-5 py-4 sm:px-7" style={{ borderColor: colors.borderSubtle }}>
        <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.18em]" style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}>
          <Sparkles className="h-3 w-3" />
          Beta
        </div>
      </div>

      {slide.kind === 'message' ? (
        /* Message slides: progress bar pinned top, content vertically centered, footer pinned bottom */
        <>
          <div className="flex-shrink-0 border-b px-5 py-4 sm:px-7" style={{ borderColor: colors.borderSubtle }}>
            {progressBar}
          </div>
          <div className="flex-1 flex flex-col items-start justify-center px-8 sm:px-12">
            <AnimatePresence mode="popLayout" initial={false}>
              <Motion.div
                key={`message-${step}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
              >
                <h2 className="text-3xl font-semibold tracking-tight sm:text-[2.6rem]" style={{ color: colors.textPrimary }}>
                  {slide.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed sm:text-lg" style={{ color: colors.textSecondary }}>
                  {slide.body}
                </p>
              </Motion.div>
            </AnimatePresence>
          </div>
          <div className="flex-shrink-0 flex items-center justify-between border-t px-5 py-4 sm:px-7" style={{ borderColor: colors.borderSubtle }}>
            {slide.secondaryAction ? (
              <button
                type="button"
                onClick={() => onSelect(slide.secondaryAction!.value)}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all cursor-pointer"
                style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
              >
                {slide.secondaryAction.label}
              </button>
            ) : (
              <button
                type="button"
                onClick={onBack}
                disabled={!canGoBack || disabled}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-35 cursor-pointer"
                style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary }}
                onMouseEnter={(e) => { if (canGoBack && !disabled) { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; } }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => onSelect(slide.primaryAction.value)}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all cursor-pointer"
              style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
            >
              {slide.primaryAction.label}
            </button>
          </div>
        </>
      ) : (
        /* Question slides: step bar + title + choices */
        <>
          <div className="flex-shrink-0 border-b px-5 py-4 sm:px-7" style={{ borderColor: colors.borderSubtle }}>
            <div className="mb-3 flex items-center justify-between gap-3">
              {progressBar}
            </div>
            <AnimatePresence mode="popLayout" initial={false}>
              <Motion.div
                key={`question-${step}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
              >
                <h2 className="text-2xl font-semibold tracking-tight sm:text-[2rem]" style={{ color: colors.textPrimary }}>
                  {slide.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed sm:text-base" style={{ color: colors.textSecondary }}>
                  {slide.prompt}
                </p>
              </Motion.div>
            </AnimatePresence>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
            <Motion.div
              key={`choices-${step}`}
              className="grid grid-cols-2 gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.22 }}
            >
              {slide.choices.map((choice) => {
                const selected = slide.value === choice.value;
                return (
                  <Motion.button
                    key={choice.value}
                    type="button"
                    whileHover={disabled ? undefined : { scale: 1.01 }}
                    whileTap={disabled ? undefined : { scale: 0.97 }}
                    disabled={disabled}
                    onClick={() => onSelect(choice.value)}
                    className="flex items-center gap-3 rounded-md px-4 py-3 text-left transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                    style={{
                      background: selected ? colors.accentSubtle : colors.bgBase,
                      border: `1px solid ${selected ? colors.accentBorder : colors.borderDefault}`,
                      color: colors.textPrimary,
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover;
                        (e.currentTarget as HTMLButtonElement).style.background = colors.bgHover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault;
                        (e.currentTarget as HTMLButtonElement).style.background = colors.bgBase;
                      }
                    }}
                  >
                    <div
                      className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm"
                      style={{
                        border: `1px solid ${selected ? colors.accentBorder : colors.borderDefault}`,
                        background: selected ? colors.accentSubtle : 'transparent',
                      }}
                    >
                      <span
                        className="block h-2 w-2 rounded-sm"
                        style={{ background: selected ? accent : 'transparent' }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{choice.label}</div>
                    </div>
                  </Motion.button>
                );
              })}
            </Motion.div>
            {slide.value === 'Other' && onOtherInputChange && (
              <Motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="mt-3 flex gap-2"
              >
                <input
                  autoFocus
                  value={otherInputValue ?? ''}
                  onChange={(e) => onOtherInputChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOtherInputSubmit?.(); }}
                  placeholder="Type your answer…"
                  className="flex-1 px-4 py-2.5 rounded-md text-sm placeholder-[#525252] focus:outline-none transition-colors"
                  style={{
                    background: colors.bgBase,
                    border: `1px solid ${colors.accentBorder}`,
                    color: colors.textPrimary,
                  }}
                />
                <button
                  type="button"
                  onClick={onOtherInputSubmit}
                  disabled={disabled}
                  className="px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer disabled:opacity-40"
                  style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
                >
                  Continue
                </button>
              </Motion.div>
            )}
          </div>
        </>
      )}

      {/* Footer — only shown on question slides */}
      {slide.kind === 'question' && <div className="flex-shrink-0 flex items-center justify-between border-t px-5 py-4 sm:px-7" style={{ borderColor: colors.borderSubtle }}>
        <button
          type="button"
          onClick={onBack}
          disabled={!canGoBack || disabled}
          className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-35 cursor-pointer"
          style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary }}
          onMouseEnter={(e) => { if (canGoBack && !disabled) { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; } }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back
        </button>
        {slide.kind === 'question' && (
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all cursor-pointer"
            style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
          >
            Skip for now
          </button>
        )}
      </div>}
    </Motion.div>
  );
}
